import { useParams } from 'react-router-dom'
import { EarnDetailsAsset } from '../types'
import {
  EARN_DETAILS_STATE,
  EarnDetailsData,
  UseEarnDetailsDataHandlers,
} from '../types/detailsState'
import { formatAPY, formatUsd } from '../utils/utils'
import { useEarnDetailsQuery } from './api/useEarnDetailsQuery'
import { useEarnStatisticsQuery } from './api/useEarnStatisticsQuery'
import { useEarnModel } from './useEarnModel'

export const useEarnDetailsData = ({
  onDeposit,
  onWithdraw,
}: UseEarnDetailsDataHandlers): EarnDetailsData => {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const { data, isLoading } = useEarnDetailsQuery(portfolioId)
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()
  const earnModel = useEarnModel()

  const common = { isStatisticsLoading, earnModel, portfolioId }

  if (isLoading) {
    return { ...common, detailsState: EARN_DETAILS_STATE.LOADING }
  }

  if (!data) {
    return { ...common, detailsState: EARN_DETAILS_STATE.NOT_FOUND }
  }

  const merchantStatistics = statistics?.currencies[data.asset]
  const assetWithPortfolio: EarnDetailsAsset = { ...data, portfolioId }

  return {
    ...common,
    detailsState: EARN_DETAILS_STATE.READY,
    asset: data.asset,
    walletId: data.walletId,
    epcTakesEarn: data.epcTakesEarn,
    earnRate: formatAPY(data.myEarnRate ?? 0),
    customerEarnRate: formatAPY(data.customerEarnRate ?? 0),
    summary: {
      allocatedFunds: formatUsd(data.fundsOverview.allocatedFunds),
      unallocatedFunds: formatUsd(merchantStatistics?.merchantMovableBalance.amount),
    },
    totalEarnings: {
      allocationEarnings: formatUsd(data.totalEarnings.fromMyAllocation),
      customerEarnings: formatUsd(data.totalEarnings.fromCustomers),
      totalEarnings: formatUsd(data.totalEarnings.total),
    },
    customerSummary: {
      customerEarningBalance: formatUsd(merchantStatistics?.optIn.earningBalance?.amount),
      customerUnpaidAmount: formatUsd(merchantStatistics?.customerRewardsUnpaid.amount),
      customerPaidAmount: formatUsd(merchantStatistics?.customerRewardsPaid.amount),
    },
    onDeposit: () => onDeposit(assetWithPortfolio),
    onWithdraw: () => onWithdraw(assetWithPortfolio),
  }
}
